import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Genders } from '@anytag/graphql';
import { warningYellowImage, signedUpImage } from '@anytag/shared-assets';
import { Anchor, AnchorPropsInternal, TTCMBadge, Tooltip } from '@anytag/shared-atoms';
import { useTranslateCountry } from '@anytag/shared-hooks';
import { cn, formatIntNumber, getGenderString } from '@anytag/shared-utils';
import { SpreadButton, SpreadButtonProps } from '../spread-button';

export interface InfluencerWidgetInformationProps {
  name: string;
  className?: string;
  age?: number | null;
  isWarning?: boolean;
  gender: Genders | '';
  isSignedUp?: boolean;
  children?: ReactNode;
  isTTCMCreator?: boolean;
  customButton?: ReactNode;
  spreadButtonProps?: SpreadButtonProps;
  country: { id?: string; name?: string };
  profileUrl?: Pick<AnchorPropsInternal, 'href' | 'params' | 'search' | 'target' | 'disabled'>;
}

export const InfluencerWidgetInformation = ({
  age,
  name,
  gender,
  country,
  children,
  isWarning,
  className,
  isSignedUp,
  profileUrl,
  isTTCMCreator,
  spreadButtonProps,
  customButton = null
}: InfluencerWidgetInformationProps) => {
  const { t } = useTranslation();
  const { tc } = useTranslateCountry();

  const info = [
    { label: 'HeaderColumn.Country', value: tc(country.name as string) },
    { label: 'HeaderColumn.Gender', value: t(`Option.${getGenderString(gender as Genders)}`) },
    { label: 'HeaderColumn.Age', value: formatIntNumber(age) }
  ];

  return (
    <div className={cn(styles.infoWrapper, className)}>
      <div className={styles.nameWrapper}>
        <Anchor target="_blank" label={name} className={styles.name} {...profileUrl} />
        {isSignedUp && (
          <Tooltip
            help={t(
              isWarning ? 'Tooltip.Account is disconnected' : 'Tooltip.This Influencer is Signed Up to AnyCreator'
            )}
          >
            <img
              src={isWarning ? warningYellowImage : signedUpImage}
              className={cn(styles.profileStatus({ isWarning }))}
              alt="profile status"
            />
          </Tooltip>
        )}
        {isTTCMCreator ? <TTCMBadge>{t('TTCM')}</TTCMBadge> : null}
        {spreadButtonProps ? <SpreadButton {...spreadButtonProps} className="ml-auto" /> : customButton}
      </div>
      <div className={cn('info-group', styles.infoGroup)}>
        {info.map(({ label, value }) => (
          <div key={label}>
            <div title={t<string>(label)} className={cn('label', styles.label)}>
              {t(label)}
            </div>
            <div title={value} className={cn('value', styles.value)}>
              {value}
            </div>
          </div>
        ))}
      </div>
      {children}
    </div>
  );
};

const styles = {
  infoWrapper: cn('box-content w-53 overflow-hidden border-r-border px-4'),
  nameWrapper: cn('flex w-full items-center'),
  name: cn('text-lg leading-4.5 font-semibold'),
  label: cn('overflow-hidden text-sm leading-4 tracking-tighter text-ellipsis whitespace-nowrap text-tertiary'),
  value: cn('overflow-hidden leading-4 text-ellipsis whitespace-nowrap text-primary'),
  infoGroup: cn(
    'mt-2.5 flex justify-between font-normal',
    '[&_.label]:text-xs [&_.value]:text-sm',
    '[&>div]:nth-of-type-1:max-w-[40%] [&>div]:nth-of-type-1:flex-[40%_0_0] [&>div]:nth-of-type-2:max-w-[40%] [&>div]:nth-of-type-2:flex-[40%_0_0]'
  ),
  profileStatus: ({ isWarning }: Pick<InfluencerWidgetInformationProps, 'isWarning'>) =>
    cn('mr-1 ml-0.25 h-4 w-4 cursor-pointer self-center', {
      'cursor-default': !isWarning
    })
};
